/**
 * 设置带过期时间的数据
 */
function setValueWithExpiry(key: string, value: unknown, expirySeconds: number = 3600 * 24 * 7): void {
  const item = {
    value,
    expiry: Date.now() + expirySeconds * 1000,
  };
  localStorage.setItem(key, JSON.stringify(item));
}

/**
 * 获取数据时检查过期
 */
function getValueWithExpiry<T = unknown>(key: string): T | null {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return null;

  let item: { value: unknown; expiry: number };
  try {
    item = JSON.parse(itemStr);
  } catch {
    // 非 JSON 格式的旧数据或损坏数据，跳过
    return null;
  }

  if (Date.now() > item.expiry) {
    localStorage.removeItem(key);
    return null;
  }
  return item.value as T;
}

/**
 * 更新值，保留原有过期时间（key 不存在或格式无效时不写入）
 */
function setValueKeepExpiry(key: string, value: unknown): boolean {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return false;
  try {
    const item = JSON.parse(itemStr);
    if (item && typeof item === 'object' && 'expiry' in item) {
      item.value = value;
      localStorage.setItem(key, JSON.stringify(item));
      return true;
    }
  } catch {
    // 非 JSON 格式的值跳过
  }
  return false;
}

/**
 * 更新过期时间
 */
function updateExpiry(key: string, expirySeconds: number): void {
  const itemStr = localStorage.getItem(key);
  if (!itemStr) return;

  try {
    const item = JSON.parse(itemStr);
    if (item && typeof item === 'object' && 'value' in item) {
      item.expiry = Date.now() + expirySeconds * 1000;
      localStorage.setItem(key, JSON.stringify(item));
    }
  } catch {
    // 非 JSON 格式的值跳过
  }
}

/**
 * 获取用户特定的 projectId key
 * 格式：lastProjectId_${userid}
 */
function getUserProjectIdKey(userId?: string | null): string | null {
  const resolved = userId ?? getValueWithExpiry<string>('userid');
  return resolved ? `lastProjectId_${resolved}` : null;
}

/**
 * 保存用户的最后访问项目（用户绑定）
 */
function setProjectId(projectId: string | number, userId?: string | null): void {
  const key = getUserProjectIdKey(userId);
  if (key && projectId) {
    setValueWithExpiry(key, String(projectId), 3600 * 24 * 7);
  }
}

function getStoredProjectIdValue(key: string): string | null {
  const stored = localStorage.getItem(key);
  if (!stored) return null;

  try {
    const parsed = JSON.parse(stored);
    if (typeof parsed === 'number' && Number.isInteger(parsed) && parsed > 0) {
      return String(parsed);
    }
    if (!parsed || typeof parsed !== 'object' || !('value' in parsed)) {
      return null;
    }
    if (typeof parsed.expiry !== 'number' || Date.now() > parsed.expiry) {
      localStorage.removeItem(key);
      return null;
    }
    const value = String(parsed.value ?? '').trim();
    return /^\d+$/.test(value) ? value : null;
  } catch {
    const value = stored.trim();
    return /^\d+$/.test(value) ? value : null;
  }
}

/**
 * 获取用户的最后访问项目（用户绑定）
 */
function getProjectId(userId?: string | null): string | null {
  const key = getUserProjectIdKey(userId);
  if (key) {
    const projectId = getStoredProjectIdValue(key);
    if (projectId) return projectId;
  }

  // 向后兼容：尝试读取旧格式（不带 userid 的）
  const legacyProjectId =
    getStoredProjectIdValue('lastProjectId') ??
    getStoredProjectIdValue('currentProjectId');

  if (legacyProjectId && userId) {
    // 自动迁移到新格式
    setProjectId(legacyProjectId, userId);
    localStorage.removeItem('lastProjectId');
    localStorage.removeItem('currentProjectId');
    return legacyProjectId;
  }

  return legacyProjectId;
}

/**
 * 清除当前用户的 lastProjectId
 */
function clearUserProjectId(userId?: string | null): void {
  const key = getUserProjectIdKey(userId);
  if (key) {
    localStorage.removeItem(key);
  }
}

/**
 * 获取当前用户 ID
 */
function getUserId(): string | null {
  return getValueWithExpiry<string>('userid');
}

export default {
  setValueWithExpiry,
  setValueKeepExpiry,
  getValueWithExpiry,
  updateExpiry,
  setProjectId,
  getProjectId,
  getUserId,
  clearUserProjectId,
  removeValue: (key: string) => localStorage.removeItem(key),
};
